import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    View,
    Animated,
    Dimensions,
    PanResponder,
    Text,
    StyleSheet,
} from 'react-native';
import { ImageComp } from '../Image/Image';
import { ARROW_RIGHT } from '../../assets';
import { useCssStyle } from '../../customHook/getCssStyle';
import triggerHapticFeedback from '../../js/vibration/index';

const SCREEN_WIDTH = Dimensions.get('window').width;
const THUMB_SIZE = 50;

const SliderPressable = props => {

    const { text, backgroundText, disabled=false, handleOnSuccess } = props || {};

    const getStyles = useCssStyle();

    const translateX = useRef(new Animated.Value(0)).current;
    const translateX_offsetWidth = useRef(new Animated.Value(10)).current;

    const containerWidthRef = useRef(0);
    const containerFullWidthRef = useRef(SCREEN_WIDTH);
    const handleOnSuccessRef = useRef(handleOnSuccess);
    const disabledRef = useRef(disabled);

    useEffect(() => {
        handleOnSuccessRef.current = handleOnSuccess;
    }, [handleOnSuccess]);

    useEffect(() => {
        disabledRef.current = disabled;
    }, [disabled]);

    const handleSliderSuccess = useRef(() => {
        if (!disabledRef.current) {
            handleOnSuccessRef.current?.();
        }
    }).current;

    const panResponder = useRef(
        PanResponder.create({
            onStartShouldSetPanResponder: () => true,
            onStartShouldSetPanResponderCapture: () => false,
            onMoveShouldSetPanResponder: (_, gestureState) => {
                return Math.abs(gestureState.dx) > Math.abs(gestureState.dy);
            },
            onMoveShouldSetPanResponderCapture: (_, gestureState) => {
                return Math.abs(gestureState.dx) > Math.abs(gestureState.dy);
            },
            onPanResponderTerminationRequest: () => false,
            onPanResponderMove: (_, gestureState) => {
                const maxX = containerWidthRef.current;
                const newX = Math.min(Math.max(0, gestureState.dx), maxX);
                translateX.setValue(newX);
            },
            onPanResponderRelease: (_, gestureState) => {
                const maxX = containerWidthRef.current;

                if (gestureState.dx > maxX * 0.6) {
                    triggerHapticFeedback("effectHeavyClick");
                    Animated.timing(translateX, {
                        toValue: maxX,
                        duration: 200,
                        useNativeDriver: false,
                    }).start(() => {
                        setTimeout(() => {
                            handleSliderSuccess();
                        }, 10);
                        setTimeout(() => {
                            Animated.spring(translateX, {
                                toValue: 0,
                                useNativeDriver: false,
                                overshootClamping: true,
                            }).start();
                        }, 200);
                    });
                } else {
                    Animated.spring(translateX, {
                        toValue: 0,
                        useNativeDriver: false,
                        overshootClamping: true,
                    }).start();
                }
            },
        }),
    ).current;

    const backgroundColorEffect = getStyles(["customSliderFillAnimated", disabled ? "customSliderFillAnimatedDisabled" : {}])
    const backgroundColor = getStyles(["customSliderFill", disabled ? "customSliderFillDisabled" : {}]);

    const animatedBackgroundColor = translateX.interpolate({
        inputRange: [0, Math.max(containerWidthRef.current || SCREEN_WIDTH - THUMB_SIZE, 0)],
        outputRange: [backgroundColor?.backgroundColor, backgroundColorEffect?.backgroundColor],
        extrapolate: 'clamp',
    });

    return (
        <View style={getStyles(['customSliderContainer'])} 
            id='customSliderContainer'
            onLayout={(event) => {
                const { width } = event.nativeEvent.layout;
                containerWidthRef.current = width - THUMB_SIZE;
                containerFullWidthRef.current = width;
            }}
        >

        <Animated.View
            style={getStyles([
                'customSliderFill',
                {
                    width: Animated.add(translateX,translateX_offsetWidth),
                    backgroundColor: animatedBackgroundColor,
                    overflow: 'hidden',
                }
            ])}
            id={"customSliderFill customSliderFillAnimated"}
        >
            {backgroundText ? (
                <Text
                    style={[
                        {width: containerFullWidthRef.current},
                        getStyles(['customSliderTextBackdrop', disabled ? "customSliderTextBackdropDisabled" : {}]),
                    ]}
                >
                    {backgroundText}
                </Text>
            ) : null}
        </Animated.View>

            <Text style={getStyles(['customSliderText'])} id='customSliderText'>{text}</Text>
            <Animated.View
                {...panResponder.panHandlers}
                style={[
                    getStyles(['customSliderThumb']),
                    {
                        transform: [{ translateX }],
                    },
                ]}
                id="customSliderThumb"
            >
                <ImageComp
                    source={ARROW_RIGHT}
                    style={getStyles(['customSliderArrowIcon'])}
                    id="customSliderArrowIcon"
                />
            </Animated.View>
        </View>
    );
};

export default SliderPressable;
